import React, { useMemo } from 'react';
import { usePromotionData } from '../hooks/usePromotionData';
import { usePromotionForm } from '../hooks/usePromotionForm';
import { usePromotionModal } from '../hooks/usePromotionModal';
import SettlementSection from './SettlementSection';
import MissionRegisterForm from './MissionRegisterForm';
import MissionList from './MissionList';
import MissionDetailModal from './MissionDetailModal';
import '../styles/PromotionSupplierToRiderPage.css';

const PromotionSupplierToRiderPage: React.FC = () => {
    const {
        isReadOnly, suppId, today,
        missions, missionsLoading, fetchMissions,
        riders, ridersLoading, ridersLoaded, fetchRiders,
        missionTypeCodes, periodCodes,
        settlementStartDate, setSettlementStartDate,
        settlementEndDate, setSettlementEndDate,
        settlements, settlementsLoading, fetchSettlements,
        handleForceProcess, handleSwOnly,
    } = usePromotionData();

    const {
        form, editingSeqNo, riderSearch, setRiderSearch, isValid,
        setField, handleMissionTypeChange, toggleRider,
        handleRegister, handleReset, handleDelete, handleStatusToggle,
        handleEditStart, handleUpdate,
    } = usePromotionForm({
        missions, suppId, isReadOnly, riders, ridersLoaded, fetchRiders, fetchMissions,
    });

    const {
        selectedMission, setSelectedMission,
        riderResults, riderResultsLoading,
        expandedRiderIds, setExpandedRiderIds,
        resultRiderMap,
    } = usePromotionModal({ today, ridersLoaded, ridersLoading, fetchRiders });

    const activeMissions = useMemo(
        () => missions.filter((m) => m.endDate >= today),
        [missions, today]
    );
    const activeCount = useMemo(
        () => activeMissions.filter((m) => m.status === 'ACTIVE').length,
        [activeMissions]
    );
    const endedMissions = useMemo(
        () => missions.filter((m) => m.endDate < today),
        [missions, today]
    );

    return (
        <div className="supplier-selection-page">
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 미션 설정 (협력사 → 라이더 프로모션)</h1>
                <p className="page-subtitle">
                    소속 라이더에게 인센티브 미션을 부여합니다. 기간(하루/일주일) 동안 목표 건수를
                    달성하면 보상이 지급됩니다.
                </p>
            </div>

            {isReadOnly && (
                <div className="mission-readonly-banner">
                    <i className="fas fa-info-circle" />
                    본사 계정으로는 미션 등록이 불가합니다. 미션 등록은 협력사 계정으로 로그인 후
                    이용하세요.
                </div>
            )}

            {isReadOnly && (
                <SettlementSection
                    settlementStartDate={settlementStartDate}
                    settlementEndDate={settlementEndDate}
                    settlements={settlements}
                    settlementsLoading={settlementsLoading}
                    onStartDateChange={setSettlementStartDate}
                    onEndDateChange={setSettlementEndDate}
                    onFetchSettlements={fetchSettlements}
                    onForceProcess={handleForceProcess}
                    onSwOnly={handleSwOnly}
                />
            )}

            {!isReadOnly && (
                <MissionRegisterForm
                    form={form}
                    editingSeqNo={editingSeqNo}
                    missionTypeCodes={missionTypeCodes}
                    periodCodes={periodCodes}
                    riders={riders}
                    ridersLoading={ridersLoading}
                    ridersLoaded={ridersLoaded}
                    riderSearch={riderSearch}
                    isValid={isValid}
                    today={today}
                    onMissionTypeChange={handleMissionTypeChange}
                    onFieldChange={setField}
                    onRiderSearchChange={setRiderSearch}
                    onToggleRider={toggleRider}
                    onFetchRiders={fetchRiders}
                    onRegister={handleRegister}
                    onUpdate={handleUpdate}
                    onReset={handleReset}
                />
            )}

            <MissionList
                activeMissions={activeMissions}
                endedMissions={endedMissions}
                missionsLoading={missionsLoading}
                activeCount={activeCount}
                isReadOnly={isReadOnly}
                missionTypeCodes={missionTypeCodes}
                periodCodes={periodCodes}
                onRefresh={fetchMissions}
                onSelectMission={setSelectedMission}
                onEditStart={handleEditStart}
                onStatusToggle={handleStatusToggle}
                onDelete={handleDelete}
            />

            {selectedMission && (
                <MissionDetailModal
                    mission={selectedMission}
                    today={today}
                    isReadOnly={isReadOnly}
                    missionTypeCodes={missionTypeCodes}
                    periodCodes={periodCodes}
                    riders={riders}
                    riderResults={riderResults}
                    riderResultsLoading={riderResultsLoading}
                    expandedRiderIds={expandedRiderIds}
                    resultRiderMap={resultRiderMap}
                    onClose={() => setSelectedMission(null)}
                    onEditStart={(m) => {
                        handleEditStart(m);
                        setSelectedMission(null);
                    }}
                    onStatusToggle={async (m) => {
                        await handleStatusToggle(m);
                        setSelectedMission(null);
                    }}
                    onDelete={async (seqNo) => {
                        setSelectedMission(null);
                        await handleDelete(seqNo);
                    }}
                    onToggleExpand={(riderId) =>
                        setExpandedRiderIds((prev) => {
                            const next = new Set(prev);
                            next.has(riderId) ? next.delete(riderId) : next.add(riderId);
                            return next;
                        })
                    }
                />
            )}
        </div>
    );
};

export default PromotionSupplierToRiderPage;
